import React, { useEffect, useMemo, useState } from 'react';
import { FlexColumn, InnerContainer, RealmList } from './styles';
import { get, size, set, setWith } from 'global/lib/lodash';
import PermissionGate from 'global/components/permission-gate';
import { Box, Checkbox, FormControlLabel, FormGroup, FormHelperText, Icon, Typography } from '@mui/material';
import Styles, { Loader } from 'global/styles';
import Droprealms, { Dropitems, getRealmIcon } from 'global/components/droprealms';
import { getUserAppPermissions, reverseUserAppPermissions, cleanCopy, uppercaseFirst } from 'global/utils';
import Confirm from 'global/components/confirm';
import sendData from 'redux/actions/sendData';
import { useVendors, useClients } from 'global/hooks/useVendors';
import useNavSettings from 'global/hooks/useNavSettings';
import { useUser } from 'global/hooks/useUser';
import useRetrieveData from 'global/hooks/useRetrieveData';
import Panels from '../../../global/components/panels';
import { GridContainer, CenterContent } from 'global/components/perform-grid';
import { EmptyPlaceholder, FlexGridDataTable, FlexGridIconLabel, FlexGridListHint, listIconColor, listIconSx } from 'global/components/flex-grid-list';
import Modal from 'global/components/modal';

const CLIENT_SHARE_HELPER =
  'Clients must grant permissions and access; vendors cannot edit them for their clients.';

const TIER_LABEL = {
  builder: { one: 'Builder', many: 'Builders' },
  division: { one: 'Division', many: 'Divisions' },
  community: { one: 'Community', many: 'Communities' },
};

const sortRealmNames = (_a, _b, me) => {
  const userRealmId = get(me, 'realmId');
  let keyA = 'client';
  let keyB = 'client';
  if (userRealmId === get(_a, keyA + '.id')) keyA = 'vendor';
  if (userRealmId === get(_b, keyB + '.id')) keyB = 'vendor';
  const a = get(_a, keyA + '.name', '').toLowerCase();
  const b = get(_b, keyB + '.name', '').toLowerCase();
  return a === b ? 0 : a > b ? 1 : -1;
};

const itemsToIdMap = (items) => {
  const map = {};
  (items || []).forEach((item) => {
    if (item?.id != null) set(map, item.id, item);
  });
  return map;
};

const selectedFromDraft = (realm, type, catalog, selItems) => {
  const byId = {};
  [...get(realm, type, []), ...(catalog || [])].forEach((item) => {
    if (item?.id != null) byId[item.id] = item;
  });
  return Object.keys(get(selItems, [realm.id, type], {}))
    .filter((id) => !!get(selItems, [realm.id, type, id]))
    .map((id) => byId[id] || byId[String(id)])
    .filter(Boolean);
};

const AccessSummary = ({ realms }) => {
  const byTier = {};
  (realms || []).filter(Boolean).forEach((r) => set(byTier, r.tier, [...get(byTier, r.tier, []), r]));
  const tiers = ['builder', 'division', 'community'].filter((tier) => size(byTier[tier]));
  if (!tiers.length) {
    return <EmptyPlaceholder>Any</EmptyPlaceholder>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {tiers.map((tier) => {
        const count = size(byTier[tier]);
        const labels = TIER_LABEL[tier];
        const label = `${count} ${count > 1 ? labels.many : labels.one}`;
        return (
          <FlexGridIconLabel key={tier} icon={getRealmIcon({ tier })} title={label}>
            {label}
          </FlexGridIconLabel>
        );
      })}
    </Box>
  );
};

const NamesSummary = ({ items, icon, empty = 'None' }) => {
  if (!size(items)) {
    return <EmptyPlaceholder>{empty}</EmptyPlaceholder>;
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {items.map((item) => (
        <FlexGridIconLabel key={item.id} icon={icon} title={item.name}>
          {item.name}
        </FlexGridIconLabel>
      ))}
    </Box>
  );
};

export const VendorList = (props) => {
  const { data, nameType } = props;
  const [permState, setPermState] = useState({});
  const [draftRealms, setDraftRealms] = useState({});
  const [confirmModal, setConfirmModal] = useState();
  const [loading, setLoading] = useState(props.loading);
  const [saving, setSaving] = useState(false);
  const [editRealm, setEditRealm] = useState(null);
  const [selSnapshot, setSelSnapshot] = useState(null);
  const { filterPermissionsByNav } = useNavSettings();
  const { me } = useUser();
  const myRealmId = get(me, 'realm.id');
  const [selItems, setSelItems] = useState({});
  const styles = Styles();

  const canEdit = get(props, 'canEdit', true);
  const isVendor = nameType === 'vendor';
  const itemToUse = get(props, 'nameType', 'client');
  const showName = get(props, 'showName', true);
  const permItems = ['createTasks', 'createReports', 'createLibrary'];

  useEffect(() => {
    setLoading(props.loading);
    // Don't clobber in-modal draft selection while an edit is open.
    if (!props.loading && !editRealm) {
      const items = {};
      (data || []).forEach((d) => {
        const { editions, scorecards, id } = d;
        (editions || []).forEach((e) => setWith(items, [id, 'editions', `${e.id}`], true, Object));
        (scorecards || []).forEach((e) => setWith(items, [id, 'scorecards', `${e.id}`], true, Object));
      });
      setSelItems(items);
    }
  }, [props.loading, data, editRealm]);

  const getSelector = (realm, perm) => `${realm.id}.${perm.value}`;

  const getRealmName = (realm) => {
    const realmname = get(realm, [itemToUse, 'name']);
    return props.namePrefix ? `${props.namePrefix}${realmname}` : realmname;
  };

  const openEdit = (realm) => {
    const s = {};
    (getUserAppPermissions(realm.permissions) || []).forEach((perm) => set(s, getSelector(realm, perm), true));
    setPermState(s);
    setDraftRealms(itemsToIdMap(get(realm, 'realms', [])));
    setSelSnapshot(cleanCopy(selItems));
    setEditRealm(realm);
  };

  const closeEdit = ({ restore = true } = {}) => {
    if (restore && selSnapshot) setSelItems(selSnapshot);
    setEditRealm(null);
    setPermState({});
    setDraftRealms({});
    setSelSnapshot(null);
  };

  const onDelete = (realm) => {
    setConfirmModal({
      title: `Delete ${uppercaseFirst(itemToUse)}?`,
      content: `Are you sure you want to delete ${get(realm, [itemToUse, 'name'])}?`,
      onCancel: () => setConfirmModal(),
      onOk: async () => {
        setLoading(true);
        setConfirmModal();
        closeEdit({ restore: false });
        await sendData(
          {
            url: `/client-vendors/${realm.id}`,
            method: 'patch',
            data: {
              data: {
                type: 'client-vendors',
                id: realm.id,
                attributes: { archived: true },
              },
            },
          },
          { simulate: false }
        );
        props.update();
      },
    });
  };

  const patchVendor = (realm, attributes = {}, relationships = {}) =>
    sendData(
      {
        url: `/client-vendors/${realm.id}`,
        method: 'patch',
        data: {
          data: {
            type: 'client-vendors',
            id: realm.id,
            ...(size(attributes) ? { attributes } : null),
            ...(size(relationships) ? { relationships } : null),
          },
        },
      },
      { simulate: false, normalize: { build: ['clientVendors'] } }
    );

  const handleChange = (r, e, type = 'editions') => {
    const key = [r.id, type, e.id];
    set(selItems, key, !get(selItems, key));
    setSelItems({ ...selItems });
  };

  const resetSel = (r, type) => {
    Object.keys(get(selItems, [r.id, type], {})).forEach((e) => set(selItems, [r.id, type, e], true));
    setSelItems({ ...selItems });
  };

  const pickerModalProps = (r, type) => ({
    title: `SET ${type.toUpperCase()}`,
    onClose: () => resetSel(r, type),
    content: () => (
      <FlexColumn>
        {get(props, [type, 'data'], [])
          .filter((d) => get(d, 'ownerRealmId') == myRealmId)
          .map((e) => {
            const isActive = type === 'scorecards' ? e.active != 0 : true;
            return (
              <FormControlLabel
                key={e.id}
                control={<Checkbox checked={!!get(selItems, [r.id, type, e.id])} onChange={() => handleChange(r, e, type)} />}
                label={e.name + (type === 'scorecards' && isActive ? ' (active)' : '')}
              />
            );
          })}
      </FlexColumn>
    ),
  });

  const submitEdit = async () => {
    if (!editRealm) return;
    const realm = editRealm;
    try {
      setSaving(true);
      if (isVendor) {
        const permissions = reverseUserAppPermissions(get(permState, realm.id));
        await patchVendor(realm, {
          permissions: permissions.map((p) => ({ name: `${p.match}.${myRealmId}` })),
        });
        await patchVendor(
          realm,
          {},
          {
            realms: {
              data: Object.keys(draftRealms || {}).map((k) => ({ type: 'realms', id: get(draftRealms, [k, 'id']) })),
            },
          }
        );
      }
      for (const type of ['editions', 'scorecards']) {
        const items = Object.keys(get(selItems, [realm.id, type], {})).map((k) => ({
          id: k,
          value: get(selItems, [realm.id, type, k]),
        }));
        await patchVendor(
          realm,
          {},
          {
            [type]: {
              data: items.filter((e) => !!e.value).map((e) => ({ type, id: e.id })),
            },
          }
        );
      }
      await props.update();
      closeEdit({ restore: false });
    } catch (err) {
      console.warn(err);
      alert(`Failed to update ${itemToUse}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const checkChange = (realm, perm) => {
    const s = { ...permState };
    const selector = getSelector(realm, perm);
    set(s, selector, !get(s, selector));
    setPermState(s);
  };

  const sorted = useMemo(
    () => [...(data || [])].sort((a, b) => sortRealmNames(a, b, me)),
    [data, me]
  );

  const columns = [
    showName && {
      field: 'name',
      title: 'Name',
      width: '22%',
      stackAt: 'sm',
      render: (realm) => (
        <Typography variant="body2">{getRealmName(realm)}</Typography>
      ),
    },
    {
      field: 'permissions',
      title: 'Permissions',
      width: '22%',
      stackAt: 'sm',
      render: (realm) => {
        const titles = (getUserAppPermissions(realm.permissions) || []).map((p) => p.title);
        return size(titles) ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {titles.map((t) => (
              <Typography key={t} variant="body2">
                {t}
              </Typography>
            ))}
          </Box>
        ) : (
          <EmptyPlaceholder>None</EmptyPlaceholder>
        );
      },
    },
    {
      field: 'access',
      title: 'Access',
      width: '18%',
      hideBelow: 'sm',
      render: (realm) => <AccessSummary realms={get(realm, 'realms', [])} />,
    },
    {
      field: 'editions',
      title: 'Editions',
      width: '19%',
      hideBelow: 'md',
      render: (realm) => (
        <NamesSummary items={get(realm, 'editions', [])} icon={<Icon fontSize="small">auto_stories</Icon>} />
      ),
    },
    {
      field: 'scorecards',
      title: 'Scorecards',
      width: '19%',
      hideBelow: 'md',
      render: (realm) => (
        <NamesSummary items={get(realm, 'scorecards', [])} icon={<Icon fontSize="small">assignment</Icon>} />
      ),
    },
  ].filter(Boolean);

  return (
    <RealmList style={props.style}>
      <InnerContainer className="vendorsInnerContainer">
        {loading && (
          <div className={styles.centerColumn}>
            <Loader />
          </div>
        )}
        {!loading && !size(data) && <Typography variant="subtitle1">None</Typography>}
        {!loading && !!size(data) && (
          <>
            {canEdit && <FlexGridListHint />}
            <FlexGridDataTable
              items={sorted}
              columns={columns}
              onSelectRow={canEdit ? openEdit : undefined}
            />
          </>
        )}
      </InnerContainer>

      <Modal
        open={!!editRealm}
        onClose={() => closeEdit()}
        title={
          editRealm
            ? `Edit ${isVendor ? 'Vendor' : 'Client'}: ${getRealmName(editRealm)}`
            : ''
        }
        maxWidth="sm"
        fullWidth
        dialogActions={{ sx: { px: 3, pb: 2 } }}
        buttons={[
          {
            title: 'Delete',
            variant: 'text',
            color: 'error',
            disabled: saving,
            onClick: () => onDelete(editRealm),
            sx: { mr: 'auto' },
          },
          { title: 'Cancel', variant: 'text', color: 'secondary', disabled: saving, onClick: () => closeEdit() },
          { title: 'Submit', variant: 'contained', color: 'secondary', disabled: saving, onClick: submitEdit },
        ]}
      >
        {editRealm && (
          <div>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Permissions & Access
            </Typography>
            <FormGroup size="small" sx={{ mb: 0.5 }}>
              {filterPermissionsByNav()
                .filter((f) => permItems.includes(f.value))
                .map((perm) => (
                  <FormControlLabel
                    key={perm.value}
                    control={
                      <Checkbox
                        size="small"
                        disabled={!isVendor}
                        checked={!!get(permState, getSelector(editRealm, perm))}
                        onChange={() => checkChange(editRealm, perm)}
                        name={perm.value}
                      />
                    }
                    label={perm.title}
                  />
                ))}
            </FormGroup>

            <Droprealms
              icon={<Icon sx={{ color: listIconColor }}>place</Icon>}
              title={(() => {
                const realms = isVendor ? Object.values(draftRealms || {}) : get(editRealm, 'realms', []);
                const n = size(realms);
                return <Box component="span" sx={{ ml: 0.5 }}>{`${n} Location${n !== 1 ? 's' : ''}`}</Box>;
              })()}
              realms={isVendor ? Object.values(draftRealms || {}) : get(editRealm, 'realms', [])}
              onUpdate={isVendor ? (sel) => setDraftRealms(sel || {}) : undefined}
              showModify={isVendor}
              renderItem={(p, i, { TitleRow, RealmText }) => (
                <TitleRow key={p.id ?? i}>
                  <Box sx={{ p: 1, ...listIconSx }}>{getRealmIcon(p)}</Box>
                  <RealmText>{get(p, 'name', '')}</RealmText>
                </TitleRow>
              )}
            />
            {!isVendor && (
              <FormHelperText sx={{ mb: 2, mt: 1 }}>{CLIENT_SHARE_HELPER}</FormHelperText>
            )}

            <Typography variant="subtitle2" sx={{ mb: 0.5, mt: 2 }}>
              Content
            </Typography>
            <Dropitems
              items={selectedFromDraft(editRealm, 'editions', get(props, 'editions.data', []), selItems)}
              modalProps={pickerModalProps(editRealm, 'editions')}
              onUpdate={() => {}}
              type="Edition"
              icon={<Icon sx={{ color: listIconColor }}>auto_stories</Icon>}
              showModify
              renderItem={(p, i, { TitleRow, RealmText }) => (
                <TitleRow key={p.id ?? i}>
                  <Box sx={{ p: 1, ...listIconSx }}>
                    <Icon fontSize="small">auto_stories</Icon>
                  </Box>
                  <RealmText>{get(p, 'name', '')}</RealmText>
                </TitleRow>
              )}
            />
            <Dropitems
              items={selectedFromDraft(editRealm, 'scorecards', get(props, 'scorecards.data', []), selItems)}
              modalProps={pickerModalProps(editRealm, 'scorecards')}
              onUpdate={() => {}}
              type="Scorecard"
              icon={<Icon sx={{ color: listIconColor }}>assignment</Icon>}
              showModify
              renderItem={(p, i, { TitleRow, RealmText }) => (
                <TitleRow key={p.id ?? i}>
                  <Box sx={{ p: 1, ...listIconSx }}>
                    <Icon fontSize="small">assignment</Icon>
                  </Box>
                  <RealmText>{get(p, 'name', '')}</RealmText>
                </TitleRow>
              )}
            />
          </div>
        )}
      </Modal>

      <Confirm {...confirmModal} />
    </RealmList>
  );
};

export default PermissionGate(() => {
  const vendors = useVendors(undefined, { useCache: false });
  const clients = useClients(undefined, undefined, { useCache: false });
  const editions = useRetrieveData(
    { url: '/editions' },
    { build: ['editions'], params: { filter: { status: 'archived,active', edition_type: 'course,document' } } }
  );
  const scorecards = useRetrieveData(
    { url: '/scorecards' },
    { build: ['scorecards'], params: { filter: { withInactive: 1 } } }
  );

  return (
    <GridContainer>
      <CenterContent fullWidth>
        <Panels
          defaultPanel="none"
          title="Vendors & Clients"
          panels={[
            {
              id: 'vendors',
              title: 'Vendors',
              component: (p) => (
                <VendorList {...p} editions={editions} scorecards={scorecards} {...vendors} nameType="vendor" />
              ),
            },
            {
              id: 'clients',
              title: 'Clients',
              component: (p) => <VendorList {...p} editions={editions} scorecards={scorecards} {...clients} />,
            },
          ]}
        />
      </CenterContent>
    </GridContainer>
  );
});
